// src/products/dataroom/pages/TeamsPage.js
import React, { useEffect, useState } from 'react';
import { Users, AlertCircle, Plus, RefreshCw, Building2, ChevronDown, ChevronRight, Edit2 } from 'lucide-react';
import useDataroomStore from '../state/dataroomStore';
import AddTeamModal from '../components/Modals/AddTeamModal';
import RenameModal from '../components/Modals/RenameModal';
import partyTeamService from '../services/partyTeamService';
import { supabase } from '../../../config';

const TeamsPage = ({ userRole, onMessage }) => {
  const {
    teams,
    parties,
    projects,
    loading,
    errors,
    loadAllTeams,
    loadAllParties,
    loadProjects
  } = useDataroomStore();

  const isLoadingTeams = loading?.teams || false;
  const teamsError = errors?.teams || null;

  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedParties, setExpandedParties] = useState({});
  const [renameTeam, setRenameTeam] = useState(null);
  const [projectNames, setProjectNames] = useState({}); // Cache for project names
  const canManage = userRole === 'creator' || userRole === 'support';

  const handleAddTeamClick = () => {
    if (projects.length === 0) {
      onMessage && onMessage('Please create a Dealroom project first before adding teams', 'warning');
      return;
    }
    setShowAddTeamModal(true);
  };

  useEffect(() => {
    loadAllTeams();
    loadAllParties();
  }, [loadAllTeams, loadAllParties]);

  // Load projects if not already loaded
  useEffect(() => {
    const loadProjectsIfNeeded = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && projects.length === 0) {
        await loadProjects(user.id);
      }
    };
    loadProjectsIfNeeded();
  }, [projects.length, loadProjects]);

  // Fetch project names for any missing projects
  useEffect(() => {
    const fetchMissingProjectNames = async () => {
      const allTeams = Object.values(teams).flat();
      const projectIds = [...new Set(allTeams.map(t => t.project_id))];
      const missingIds = projectIds.filter(id => {
        const project = projects.find(p => p.id === id);
        return !project;
      });

      if (missingIds.length > 0) {
        const { data, error } = await supabase
          .from('dataroom_projects')
          .select('id, project_name')
          .in('id', missingIds);

        if (!error && data) {
          const namesMap = {};
          data.forEach(p => {
            namesMap[p.id] = p.project_name;
          });
          setProjectNames(prev => ({ ...prev, ...namesMap }));
        }
      }
    };

    if (Object.keys(teams).length > 0) {
      fetchMissingProjectNames();
    }
  }, [teams, projects]);

  // Flatten teams data
  const allTeams = Object.values(teams).flat();

  // Build hierarchy: Project -> Party -> Teams
  const hierarchy = {};
  
  allTeams.forEach(team => {
    const projectId = team.project_id;
    const partyId = team.party_id;
    
    if (!hierarchy[projectId]) {
      hierarchy[projectId] = {};
    }
    if (!hierarchy[projectId][partyId]) {
      hierarchy[projectId][partyId] = [];
    }
    hierarchy[projectId][partyId].push(team);
  });

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const togglePartyExpansion = (partyId) => {
    setExpandedParties(prev => ({
      ...prev,
      [partyId]: !prev[partyId]
    }));
  };

  const handleRenameTeam = async (updatedTeam) => {
    try {
      const result = await partyTeamService.updateTeam(updatedTeam.id, {
        name: updatedTeam.name,
        code: updatedTeam.code,
        description: updatedTeam.description
      });

      if (result.error) throw result.error;

      onMessage && onMessage(`Team renamed successfully`, 'success');
      loadAllTeams();
    } catch (error) {
      console.error('Error renaming team:', error);
      onMessage && onMessage('Failed to rename team: ' + error.message, 'error');
      throw error;
    }
  };

  if (isLoadingTeams) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading teams...</p>
        </div>
      </div>
    );
  }

  if (teamsError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Teams</h3>
            <p className="text-sm text-red-700 mt-1">{teamsError}</p>
          </div>
        </div>
        <button
          onClick={loadAllTeams}
          className="mt-4 flex items-center px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="w-8 h-8 mr-3 text-blue-600" />
            Teams Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage teams across all parties and DealRooms
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadAllTeams}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          {canManage && (
            <button
              onClick={handleAddTeamClick}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Team
            </button>
          )}
        </div>
      </div>

      {/* Teams Grouped by DealRoom > Party */}
      {allTeams.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Teams Created</h3>
          <p className="text-gray-500 mb-6">
            Teams are permission groups within parties.
          </p>
          {canManage && (
            <button
              onClick={handleAddTeamClick}
              className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Your First Team
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(hierarchy).map(([projectId, partiesInProject]) => {
            const project = projects.find(p => p.id === parseInt(projectId));
            const projectName = project?.project_name || projectNames[parseInt(projectId)] || null;
            const isProjectExpanded = expandedProjects[projectId] !== false; // Default expanded
            const totalTeamsInProject = Object.values(partiesInProject).flat().length;
            
            return (
              <div key={projectId} className="bg-white rounded-lg shadow overflow-hidden border-2 border-gray-200">
                {/* DealRoom Header */}
                <div
                  className="bg-gradient-to-r from-blue-50 to-blue-100 border-b-2 border-blue-200 cursor-pointer"
                  onClick={() => toggleProjectExpansion(projectId)}
                >
                  <div className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center">
                      {isProjectExpanded ? (
                        <ChevronDown className="w-6 h-6 text-blue-600 mr-3" />
                      ) : (
                        <ChevronRight className="w-6 h-6 text-blue-600 mr-3" />
                      )}
                      <Building2 className="w-6 h-6 text-blue-600 mr-3" />
                      <div>
                        <h2 className="text-lg font-bold text-blue-900">
                          Dealroom: {projectName || 'Unknown Dealroom'}
                        </h2>
                        <p className="text-sm text-blue-700">
                          {totalTeamsInProject} team{totalTeamsInProject === 1 ? '' : 's'} across {Object.keys(partiesInProject).length} part{Object.keys(partiesInProject).length === 1 ? 'y' : 'ies'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Parties in this DealRoom */}
                {isProjectExpanded && (
                  <div className="divide-y divide-gray-200">
                    {(() => {
                      // Get parties for this project and sort them by name to match PartiesPage numbering
                      const projectParties = parties
                        .filter(p => p.project_id === parseInt(projectId))
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                      
                      // Create a map of party order for consistent numbering
                      const partyOrderMap = {};
                      projectParties.forEach((party, index) => {
                        partyOrderMap[party.id] = index + 1;
                      });
                      
                      // Sort party entries by party name to match PartiesPage order
                      const sortedPartyEntries = Object.entries(partiesInProject).sort(([idA], [idB]) => {
                        const partyA = parties.find(p => p.id === parseInt(idA));
                        const partyB = parties.find(p => p.id === parseInt(idB));
                        return (partyA?.name || '').localeCompare(partyB?.name || '');
                      });
                      
                      return sortedPartyEntries.map(([partyId, teamsInParty]) => {
                        const party = parties.find(p => p.id === parseInt(partyId));
                        const isPartyExpanded = expandedParties[partyId] !== false; // Default expanded
                        const partyNumber = partyOrderMap[parseInt(partyId)] || 0;
                      
                      return (
                        <div key={partyId} className="border-l-4" style={{ borderLeftColor: party?.color || '#ccc' }}>
                          {/* Party Header */}
                          <div
                            className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-3 cursor-pointer hover:from-green-100 hover:to-green-200 transition-colors"
                            onClick={() => togglePartyExpansion(partyId)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                {isPartyExpanded ? (
                                  <ChevronDown className="w-5 h-5 mr-3" style={{ color: party?.color || '#10B981' }} />
                                ) : (
                                  <ChevronRight className="w-5 h-5 mr-3" style={{ color: party?.color || '#10B981' }} />
                                )}
                                <div
                                  className="w-8 h-8 rounded-lg mr-3 flex items-center justify-center shadow-sm"
                                  style={{ backgroundColor: party?.color || '#ccc' }}
                                >
                                  <span className="text-white font-bold text-xs">{party?.code || 'P'}</span>
                                </div>
                                <div>
                                  <h3 className="text-base font-semibold text-gray-900">
                                    Party {partyNumber}: {party?.name || `Party #${partyId}`}
                                  </h3>
                                  <p className="text-sm text-green-700">
                                    {teamsInParty.length} team{teamsInParty.length === 1 ? '' : 's'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Teams in this Party */}
                          {isPartyExpanded && (
                            <div className="bg-white divide-y divide-gray-100">
                              {teamsInParty.map((team, teamIndex) => {
                                const teamNumber = teamIndex + 1;
                                return (
                                <div key={team.id} className="px-6 py-3 hover:bg-gray-50 transition-colors ml-12">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center mr-3">
                                        <Users className="w-4 h-4 text-green-600" />
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 font-mono">
                                            {team.code}
                                          </span>
                                          <h4 className="text-sm font-semibold text-gray-900">Team {teamNumber}: {team.name}</h4>
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                            <Building2 className="h-3 w-3 mr-1" />
                                            {party?.name || 'Unknown Party'}
                                          </span>
                                          {canManage && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setRenameTeam(team);
                                              }}
                                              className="text-gray-400 hover:text-green-600 transition-colors"
                                              title="Rename team"
                                            >
                                              <Edit2 className="h-3 w-3" />
                                            </button>
                                          )}
                                        </div>
                                        {team.description && (
                                          <p className="text-xs text-gray-500 mt-1">{team.description}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      {team.team_type || 'general'}
                                    </div>
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    });
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Team Modal */}
      <AddTeamModal
        isOpen={showAddTeamModal}
        onClose={() => setShowAddTeamModal(false)}
        onComplete={(addedTeams) => {
          onMessage && onMessage(`Successfully created ${addedTeams.length} team(s)`, 'success');
          loadAllTeams();
        }}
      />

      {/* Rename Team Modal */}
      <RenameModal
        isOpen={!!renameTeam}
        onClose={() => setRenameTeam(null)}
        item={renameTeam}
        itemType="team"
        onRename={handleRenameTeam}
      />
    </div>
  );
};

export default React.memo(TeamsPage);